/* Progression dictation: fill each bar with the chord you hear (first bar is given). */
import { useEffect, useState } from 'react';
import { audio, type Player } from '../../core/audio';
import { chordInKey, type KeyChord, type Mode, pretty } from '../../core/theory';
import { Btn } from '../../ui/Button';
import type { QuestionApi } from '../exercise/types';

export interface DictationUi {
  /** set by the component to highlight the bar that is playing */
  onBar: ((index: number | null) => void) | null;
}

export interface DictationProps {
  api: QuestionApi;
  degs: string[];
  chords: KeyChord[];
  palette: readonly string[];
  tonic: number;
  mode: Mode;
  ui: DictationUi;
  playBar: (index: number) => Player;
}

export function Dictation({ api, degs, chords, palette, tonic, mode, ui, playBar }: DictationProps) {
  const n = degs.length;
  const [ans, setAns] = useState<(string | null)[]>(() => degs.map((d, i) => (i === 0 ? d : null)));
  const [locked, setLocked] = useState<Set<number>>(() => new Set([0]));
  const [marks, setMarks] = useState<Record<number, 'bad'>>({});
  const [sel, setSel] = useState<number>(1);
  const [playing, setPlaying] = useState<number | null>(null);

  useEffect(() => {
    ui.onBar = setPlaying;
    return () => {
      ui.onBar = null;
    };
  }, [ui]);

  const choose = (d: string) => {
    if (api.isDone() || locked.has(sel)) return;
    const next = ans.slice();
    next[sel] = d;
    setAns(next);
    setMarks((m) => {
      const x = { ...m };
      delete x[sel];
      return x;
    });
    const c = chordInKey(d, tonic, mode);
    audio.stopAll();
    audio.playChord({ root: c.root, q: c.q }, { dur: 1.4 });
    const nextEmpty = next.findIndex((x, i) => x == null && !locked.has(i));
    if (nextEmpty >= 0) setSel(nextEmpty);
  };

  const check = () => {
    if (api.isDone()) return;
    if (ans.some((x) => x == null)) {
      api.feedback('Önce bütün ölçüleri doldur.', 'info');
      return;
    }
    const lock = new Set(locked);
    const bad: Record<number, 'bad'> = {};
    ans.forEach((a, i) => {
      if (i === 0 || a == null) return;
      if (chordInKey(a, tonic, mode).sym === chords[i]?.sym) lock.add(i);
      else bad[i] = 'bad';
    });
    setLocked(lock);
    setMarks(bad);
    const badCount = Object.keys(bad).length;
    if (!badCount) {
      api.done(true);
      return;
    }
    api.miss();
    if (api.isDone()) {
      setAns(degs.slice());
      setLocked(new Set(degs.map((_, i) => i)));
      return;
    }
    setSel(ans.findIndex((_, i) => !lock.has(i)));
    api.feedback(`${badCount} ölçü yanlış. Kırmızı ölçüleri tekrar dinleyip düzelt.`, 'bad');
  };

  return (
    <>
      <div className="dict-slots">
        {degs.map((_, i) => {
          const a = ans[i];
          const c = a ? chordInKey(a, tonic, mode) : null;
          const isLocked = locked.has(i);
          const cls = [
            'dict-slot',
            i === 0 ? 'given' : '',
            a && !isLocked ? 'filled' : '',
            isLocked && i > 0 ? 'ok' : '',
            sel === i && !isLocked ? 'sel' : '',
            marks[i] === 'bad' ? 'bad' : '',
            playing === i ? 'playing' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional
              key={i}
              type="button"
              className={cls}
              onClick={() => {
                if (isLocked) {
                  audio.stopAll();
                  playBar(i);
                  return;
                }
                setSel(i);
              }}
            >
              <span className="ds-n">{i + 1}</span>
              <span className="ds-main">{c ? pretty(c.sym) : '?'}</span>
              <span className="ds-sub">{a || ''}</span>
            </button>
          );
        })}
      </div>
      <p className="ex-hint">Bir ölçü seç, sonra aşağıdan akoru seç. Doğru bulduğun ölçüye tıklayınca o ölçü çalar.</p>
      <div className="palette sticky">
        {palette.map((d) => {
          const c = chordInKey(d, tonic, mode);
          return (
            <button key={d} type="button" className="pal-btn" onClick={() => choose(d)}>
              <span className="pal-main">{pretty(c.sym)}</span>
              <span className="pal-sub">{d}</span>
            </button>
          );
        })}
      </div>
      <div className="song-actions">
        <Btn kind="primary" icon="check" onClick={check}>
          Kontrol et
        </Btn>
        <Btn
          kind="subtle"
          icon="play"
          onClick={() => {
            if (sel >= 0 && sel < n) {
              audio.stopAll();
              playBar(sel);
            }
          }}
        >
          Seçili ölçüyü dinle
        </Btn>
      </div>
    </>
  );
}
